import { useCallback, useEffect, useRef, useState } from 'react';
import axios from '../../utils/axiosConfig';

/**
 * Custom hook for Text-to-Speech functionality
 * Uses backend TTS API to generate and play audio
 *
 * @param {Object} options
 * @param {boolean} options.enabled - Whether TTS is enabled
 * @param {string} options.voice - Voice ID for TTS (default: 'nova')
 * @param {number} options.speed - Playback speed (default: 1)
 * @param {Function} options.onSpeakingChange - Callback when speaking state changes
 * @param {Function} options.onError - Error callback
 * @returns {Object} TTS controls and state
 */
/**
 * Transient failures worth another attempt: rate limiting and upstream 5xx.
 * A 4xx other than 429 means the request itself is wrong, so retrying it just
 * delays the failure.
 */
const isRetryableStatus = (status) => status === 429 || (status >= 500 && status < 600);

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const useTextToSpeech = (options = {}) => {
  const {
    enabled = false,
    voice = 'nova',
    speed = 1,
    maxAttempts = 3,
    initialBackoffMs = 750,
    onSpeakingChange,
    onError,
  } = options;

  // State
  const [isSpeaking, setIsSpeaking] = useState(false);

  // Refs
  const audioRef = useRef(null);
  const enabledRef = useRef(enabled);
  const audioUrlsRef = useRef([]); // Track URLs for cleanup

  // Keep enabled state in sync with ref
  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  // Notify parent of speaking state changes
  useEffect(() => {
    onSpeakingChange?.(isSpeaking);
  }, [isSpeaking, onSpeakingChange]);

  /**
   * Generate audio from text using backend TTS API
   * @param {string} text - Text to convert to speech
   * @returns {Promise<Audio|null>} Audio element or null on error
   */
  const generateAudio = useCallback(
    async (text) => {
      if (!enabledRef.current || !text?.trim()) {
        return null;
      }

      // Retries on rate limits and upstream 5xx. Without this a single 429 —
      // easy to hit, since audio mode fires one request per sentence — silently
      // drops that sentence from the spoken answer.
      let backoffMs = initialBackoffMs;

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          const response = await axios.post(
            '/api/v1/anthropic/text-to-speech',
            {
              // `model` is deliberately not sent: the backend's TTSRequest
              // defaults it from ModelConfig, so a server-side TTS migration
              // isn't silently overridden by a stale client.
              text: text.trim(),
              voice,
              speed,
            },
            { responseType: 'blob' }
          );

          const audioBlob = response.data;
          const audioUrl = URL.createObjectURL(audioBlob);
          const audio = new Audio(audioUrl);

          // Pacing is applied here rather than by the TTS provider. OpenAI
          // honoured `speed` server-side; Cartesia accepts the field, validates
          // it (2.0 is a 400) and then barely acts on it — measured 2026-08-06,
          // speed=1.2 changed a 6.61s clip to 6.53s, a 1.02x change where 1.2x
          // was asked for. playbackRate is exact, costs nothing, and keeps the
          // interviewer at the same pace regardless of which provider is live.
          audio.playbackRate = speed;

          // Track URL for cleanup
          audioUrlsRef.current.push(audioUrl);

          // Cleanup URL when audio ends
          audio.addEventListener('ended', () => {
            URL.revokeObjectURL(audioUrl);
            const index = audioUrlsRef.current.indexOf(audioUrl);
            if (index > -1) {
              audioUrlsRef.current.splice(index, 1);
            }
          });

          return audio;
        } catch (err) {
          const status = err?.response?.status;

          if (attempt < maxAttempts && isRetryableStatus(status)) {
            console.warn(
              `TTS request failed with status ${status}; retrying in ${backoffMs}ms ` +
                `(attempt ${attempt} of ${maxAttempts})`
            );
            // eslint-disable-next-line no-await-in-loop
            await delay(backoffMs);
            backoffMs *= 2;
            continue;
          }

          console.error('Error generating TTS audio:', err);
          onError?.('Failed to generate speech. Please try again.');
          return null;
        }
      }

      return null;
    },
    [voice, speed, maxAttempts, initialBackoffMs, onError]
  );

  /**
   * Play an audio element
   * @param {Audio} audio - Audio element to play
   * @returns {Promise<void>} Resolves when audio finishes
   */
  const playAudio = useCallback((audio) => {
    if (!audio) {
      return Promise.reject(new Error('No audio element provided'));
    }

    setIsSpeaking(true);
    audioRef.current = audio;

    return new Promise((resolve, reject) => {
      audio.onended = () => {
        setIsSpeaking(false);
        audioRef.current = null;
        resolve();
      };

      audio.onerror = (e) => {
        setIsSpeaking(false);
        audioRef.current = null;
        reject(e);
      };

      audio.play().catch((e) => {
        setIsSpeaking(false);
        audioRef.current = null;
        reject(e);
      });
    });
  }, []);

  /**
   * Speak text (generate + play in one call)
   * @param {string} text - Text to speak
   * @returns {Promise<void>} Resolves when speaking finishes
   */
  const speak = useCallback(
    async (text) => {
      const audio = await generateAudio(text);
      if (audio) {
        return playAudio(audio);
      }
      return Promise.resolve();
    },
    [generateAudio, playAudio]
  );

  /**
   * Stop current audio playback
   */
  const stop = useCallback(() => {
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      } catch (e) {
        // Ignore errors
      }
      audioRef.current = null;
    }
    setIsSpeaking(false);
  }, []);

  /**
   * Pause current audio playback
   */
  const pause = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
  }, []);

  /**
   * Resume paused audio playback
   */
  const resume = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.play().catch(console.error);
    }
  }, []);

  /**
   * Cleanup all resources
   */
  const cleanup = useCallback(() => {
    stop();

    // Cleanup all tracked URLs
    audioUrlsRef.current.forEach((url) => {
      URL.revokeObjectURL(url);
    });
    audioUrlsRef.current = [];
  }, [stop]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    // State
    isSpeaking,

    // Controls
    generateAudio,
    playAudio,
    speak,
    stop,
    pause,
    resume,
    cleanup,

    // Refs for external access
    audioRef,
    enabledRef,
  };
};

export default useTextToSpeech;
